import * as React from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  chakra,
  Skeleton,
  HStack,
  Box,
} from "@chakra-ui/react";
import { HiChevronDown, HiChevronUp } from "react-icons/hi";
import {
  useReactTable,
  flexRender,
  getCoreRowModel,
  ColumnDef,
  SortingState,
  getSortedRowModel,
  // FilterFn,
  getFilteredRowModel,
  ColumnSort,
} from "@tanstack/react-table";

export type DataTableProps<Data extends object> = {
  data: Data[];
  columns: ColumnDef<Data, any>[];
  isLoading: boolean;
  filter?: string;
  defaultSort?: ColumnSort | null;
};

export default function DataTable<Data extends object>({
  data,
  columns,
  isLoading,
  filter = "",
  defaultSort = null,
}: DataTableProps<Data>) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const table = useReactTable({
    columns,
    data,
    getFilteredRowModel: getFilteredRowModel(),
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    enableColumnFilters: true,
    enableGlobalFilter: true,
    enableFilters: true,
    state: {
      sorting,
      globalFilter: filter,
    },
  });

  React.useEffect(() => {
    if (defaultSort) {
      setSorting([defaultSort]);
    }
  }, [defaultSort]);

  return (
    <Table>
      <Thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <Tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              // see https://tanstack.com/table/v8/docs/api/core/column-def#meta to type this correctly
              const meta: any = header.column.columnDef.meta;
              return (
                <Th
                  key={header.id}
                  onClick={header.column.getToggleSortingHandler()}
                  isNumeric={meta?.isNumeric}
                >
                  <HStack>
                    <Box>
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </Box>

                    <chakra.span>
                      {header.column.getIsSorted() ? (
                        header.column.getIsSorted() === "desc" ? (
                          <HiChevronDown aria-label="sorted descending" />
                        ) : (
                          <HiChevronUp aria-label="sorted ascending" />
                        )
                      ) : null}
                    </chakra.span>
                  </HStack>
                </Th>
              );
            })}
          </Tr>
        ))}
      </Thead>
      <Tbody>
        {isLoading && (
          <Tr>
            {table.getHeaderGroups().map((headerGroup) => {
              return headerGroup.headers.map((header) => (
                <Td key={`${headerGroup.id}`}>
                  <Skeleton isLoaded={false}>&nbsp;</Skeleton>
                </Td>
              ));
            })}
          </Tr>
        )}
        {table.getRowModel().rows.map((row) => (
          <Tr key={row.id}>
            {row.getVisibleCells().map((cell) => {
              // see https://tanstack.com/table/v8/docs/api/core/column-def#meta to type this correctly
              const meta: any = cell.column.columnDef.meta;
              return (
                <Td key={cell.id} isNumeric={meta?.isNumeric} sx={meta?.sx}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Td>
              );
            })}
          </Tr>
        ))}
      </Tbody>
    </Table>
  );
}
